import { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';

import { CompanyContext } from '../../context/company/companyContext';
import {
  IMemberFRCardBalances,
  IMemberFRConsumptionItem,
  IMemberFRCycleConsumptions,
  IMemberFRPeriodConsumptions
} from '../../interfaces/collaborators';
import { IFlexibleRemunerationPayRollCycle } from '../../interfaces/company';
import Companies from '../../services/api/collaborators/collaborators';

interface IAsyncState<T> {
  data: T;
  loading: boolean;
  error: boolean;
}

export interface IMemberFRHistoryYear {
  year: number;
  cycles: IMemberFRCycleConsumptions[];
  total: IMemberFRPeriodConsumptions;
  migrated?: IMemberFRPeriodConsumptions;
}
const aggregateYear = (cycles: IMemberFRCycleConsumptions[]): IMemberFRPeriodConsumptions => {
  const bySku = new Map<string, IMemberFRConsumptionItem>();

  cycles.forEach((cycle) => {
    cycle.consumptions.forEach((item) => {
      const acc = bySku.get(item.sku) ?? { sku: item.sku, exempt: 0, nonExempt: 0, total: 0 };
      bySku.set(item.sku, {
        sku: item.sku,
        exempt: acc.exempt + item.exempt,
        nonExempt: acc.nonExempt + item.nonExempt,
        total: acc.total + item.total
      });
    });
  });

  return {
    currency: cycles.find((cycle) => cycle.currency)?.currency ?? '',
    hasData: cycles.some((cycle) => cycle.hasData),
    consumptions: Array.from(bySku.values()),
    total: cycles.reduce((sum, cycle) => sum + cycle.total, 0),
    totalExempt: cycles.reduce((sum, cycle) => sum + cycle.totalExempt, 0),
    totalNonExempt: cycles.reduce((sum, cycle) => sum + cycle.totalNonExempt, 0)
  };
};

const groupByYear = (cycles: IMemberFRCycleConsumptions[], migratedIds: Set<string>): IMemberFRHistoryYear[] => {
  const byYear = new Map<number, IMemberFRCycleConsumptions[]>();

  cycles.forEach((cycle) => {
    if (cycle.cycleYear === null) return;
    const list = byYear.get(cycle.cycleYear) ?? [];
    list.push(cycle);
    byYear.set(cycle.cycleYear, list);
  });

  return Array.from(byYear.entries())
    .sort(([yearA], [yearB]) => yearB - yearA)
    .map(([year, list]) => {
      const sorted = [...list].sort((a, b) => (b.cycleMonth ?? 0) - (a.cycleMonth ?? 0));
      const migratedCycles = sorted.filter(({ payrollId }) => migratedIds.has(payrollId));
      return {
        year,
        cycles: sorted.filter(({ payrollId }) => !migratedIds.has(payrollId)),
        total: aggregateYear(sorted),
        ...(migratedCycles.length > 0 && { migrated: aggregateYear(migratedCycles) })
      };
    });
};

const useCollaboratorConsumptions = (memberEmail: string, enabled: boolean) => {
  const { companyData, companyRFCycles } = useContext(CompanyContext);
  const companyId = companyData.id;
  const cycles = useMemo(() => companyRFCycles?.payRollCycles ?? [], [companyRFCycles]);

  const [cardBalancesState, setCardBalancesState] = useState<IAsyncState<IMemberFRCardBalances | undefined>>({
    data: undefined,
    loading: enabled,
    error: false
  });
  const [currentCycleState, setCurrentCycleState] = useState<IAsyncState<IMemberFRCycleConsumptions | undefined>>({
    data: undefined,
    loading: enabled,
    error: false
  });
  const [historyState, setHistoryState] = useState<IAsyncState<IMemberFRCycleConsumptions[]>>({
    data: [],
    loading: enabled,
    error: false
  });

  const activeRequestRef = useRef(0);

  const fetchData = useCallback(async () => {
    if (!enabled || !companyId || !memberEmail) return;

    const requestId = ++activeRequestRef.current;
    const openCycleId = cycles[0]?.id;

    setCardBalancesState((prev) => ({ ...prev, loading: true, error: false }));
    setCurrentCycleState((prev) => ({ ...prev, loading: true, error: false }));
    setHistoryState((prev) => ({ ...prev, loading: true, error: false }));

    const loadCardBalances = async () => {
      try {
        const data = await Companies.getMemberFRCardBalances(companyId, memberEmail);
        if (activeRequestRef.current === requestId) {
          setCardBalancesState({ data, loading: false, error: false });
        }
      } catch {
        if (activeRequestRef.current === requestId) {
          setCardBalancesState({ data: undefined, loading: false, error: true });
        }
      }
    };

    const loadCycle = async (cycle: IFlexibleRemunerationPayRollCycle) => {
      try {
        const data = await Companies.getMemberFRCycleConsumptions(companyId, memberEmail, cycle.id);
        if (activeRequestRef.current === requestId && cycle.id === openCycleId) {
          setCurrentCycleState({ data, loading: false, error: false });
        }
        return data;
      } catch {
        if (activeRequestRef.current === requestId && cycle.id === openCycleId) {
          setCurrentCycleState({ data: undefined, loading: false, error: true });
        }
        return undefined;
      }
    };

    const loadConsumptions = async () => {
      if (!cycles.length) {
        if (activeRequestRef.current === requestId) {
          setCurrentCycleState({ data: undefined, loading: false, error: false });
          setHistoryState({ data: [], loading: false, error: false });
        }
        return;
      }

      const results = await Promise.all(cycles.map(loadCycle));

      if (activeRequestRef.current !== requestId) return;

      const fulfilled = results.filter((data): data is IMemberFRCycleConsumptions => data !== undefined);
      setHistoryState({ data: fulfilled, loading: false, error: fulfilled.length === 0 });
    };

    await Promise.all([loadCardBalances(), loadConsumptions()]);
  }, [enabled, companyId, memberEmail, cycles]);

  useEffect(() => {
    if (!enabled) {
      activeRequestRef.current++;
      setCardBalancesState({ data: undefined, loading: false, error: false });
      setCurrentCycleState({ data: undefined, loading: false, error: false });
      setHistoryState({ data: [], loading: false, error: false });
      return;
    }
    fetchData();
  }, [fetchData, enabled]);

  const migratedIds = useMemo(
    () => new Set(cycles.filter(({ migrated }) => migrated === true).map(({ id }) => id)),
    [cycles]
  );
  const history = useMemo(() => groupByYear(historyState.data, migratedIds), [historyState.data, migratedIds]);

  return {
    openCycle: cycles[0],
    currentCycle: currentCycleState,
    history: { data: history, loading: historyState.loading, error: historyState.error },
    cardBalances: cardBalancesState,
    refetch: fetchData
  };
};

export default useCollaboratorConsumptions;
